import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, ShieldCheck, HeartHandshake, Compass, SunMedium, Users } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';

export default function AboutIntro() {
  const { t, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  const fadeIn = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const listStagger = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const listItem = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };

  const helpItems = [
    { icon: Sparkles, text: t('about.help_items.stress') },
    { icon: ShieldCheck, text: t('about.help_items.fears') },
    { icon: SunMedium, text: t('about.help_items.burnout') },
    { icon: Compass, text: t('about.help_items.boundaries') },
    { icon: CheckCircle2, text: t('about.help_items.confidence') },
  ];

  const forWhom = [
    t('about.target_audience.0'),
    t('about.target_audience.1'),
    t('about.target_audience.2'),
  ];

  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24 bg-black">

      <div className="container-spacious">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-start">
          {/* Фото слева */}
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="order-1 lg:order-1"
          >
            <figure className="relative mx-auto w-full max-w-sm sm:max-w-md lg:max-w-none">
              <div className="relative overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="/images/me/F2.jpg"
                  alt="Альфира Херрманн: портрет гипнотерапевта"
                  className="h-[400px] sm:h-[450px] lg:h-[520px] w-full object-cover object-top"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
              </div>
              <figcaption className="mt-6">
                <div className="text-center space-y-4">
                  <h3 className="text-xl sm:text-2xl font-bold text-white tracking-wide">
                    {t('about.author_name')}
                  </h3>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1 h-1 bg-rose-400 rounded-full"></div>
                    <span className="text-sm sm:text-base text-gray-300 font-medium">
                      {t('about.author_title')}
                    </span>
                    <div className="w-1 h-1 bg-rose-400 rounded-full"></div>
                  </div>
                  
                  {/* Секция образования */}
                  <div className="mt-6 p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                    <h4 className="text-base font-semibold text-white mb-3">
                      {t('about.education.title')}
                    </h4>
                    <div className="space-y-2 text-left">
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.dhv_member')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.hypnotherapy_education')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.anxiety_training')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.business_coach')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.health_coach')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.systemic_coach')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.commercial_education')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-400 rounded-full mt-2 shrink-0"></div>
                        <p className="text-sm sm:text-base text-gray-300">
                          {t('about.education.child_psychologist')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </figcaption>
            </figure>
          </motion.div>

          {/* Текст справа */}
          <motion.div 
            variants={fadeIn} 
            initial="hidden" 
            whileInView="show" 
            viewport={{ once: true, margin: "-80px" }} 
            className="order-2 lg:order-2"
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-white">
              {t('about.title')}
            </h2>
            <p className="mt-3 text-sm sm:text-base lg:text-lg leading-relaxed text-gray-200">
              {t('about.description')}
            </p>

            <div className="mt-8">
              <div className="flex items-center gap-2">
                <HeartHandshake className="h-5 w-5 text-rose-400" aria-hidden />
                <h3 className="text-lg sm:text-xl font-semibold text-white">{t('about.help_with')}</h3>
              </div>

              <motion.ul
                variants={listStagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {helpItems.map(({ icon: Icon, text }, idx) => (
                  <motion.li
                    key={idx}
                    variants={listItem}
                    className="flex items-start gap-3 rounded-2xl border border-white/20 bg-white/10 backdrop-blur p-3 shadow-sm hover:bg-white/20 transition-colors"
                  >
                    <Icon className="mt-0.5 h-5 w-5 text-rose-400 shrink-0" aria-hidden />
                    <span className="text-gray-200">{text}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>

            <div className="mt-8">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-rose-400" aria-hidden />
                <h3 className="text-lg sm:text-xl font-semibold text-white">{t('about.for_whom')}</h3>
              </div>
              <motion.ul
                variants={listStagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="mt-4 space-y-3"
              >
                {forWhom.map((item, i) => (
                  <motion.li key={i} variants={listItem} className="flex gap-3">
                    <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-400/20 border border-rose-400/30">
                      <CheckCircle2 className="h-4 w-4 text-rose-400" aria-hidden />
                    </span>
                    <span className="text-gray-200">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>

            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3">
              <a
                href={getLocalizedPath("/contact")}
                className="inline-flex items-center justify-center rounded-2xl bg-rose-600 px-4 sm:px-5 py-3 text-white font-medium shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 focus:ring-offset-black transition-colors text-sm sm:text-base"
              >
{t('hero.cta')}
              </a>
              <p className="text-xs sm:text-sm text-gray-400">{t('hero.free_consultation')}</p>
            </div>

            {/* Правовое предупреждение */}
            <div className="mt-8 p-4 bg-red-900/20 border border-red-600/30 rounded-2xl">
              <p className="text-sm sm:text-base text-red-400 font-bold leading-relaxed">
                {t('about.legal_notice')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
